import { Injectable, OnDestroy } from '@angular/core';
import { environment } from 'src/environments/environment';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, take, timeout } from 'rxjs/operators';

type LaravelEcho = Echo<'reverb'>;

declare global {
  interface Window {
    Pusher: typeof Pusher;
    Echo: LaravelEcho;
  }
}

type ConnectionState = 'connected' | 'disconnected' | 'connecting';

@Injectable({
  providedIn: 'root'
})
export class WebSocketService implements OnDestroy {
  private echo: LaravelEcho | null = null;
  private connectionStatus$ = new BehaviorSubject<ConnectionState>('disconnected');
  private messageSubject$ = new Subject<any>();
  private userRemovedSubject$ = new Subject<any>();
  private pollUpdatedSubject$ = new Subject<any>();
  private notificationSubject$ = new Subject<any>();
  private notificationChannel: any = null;
  private currentNotificationUserId: string | number | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 3000; // 3 seconds
  private reconnectTimer: any = null;

  constructor() {
    this.initializeEcho();
  }

  private initializeEcho(): void {
    if (typeof window !== 'undefined' && !window.Pusher) {
      window.Pusher = Pusher;
    }

    const token = localStorage.getItem('token');

    const reverbConfig = environment.reverb;
    const reverbHost = reverbConfig?.host;
    const reverbPort = reverbConfig?.port;
    const reverbScheme = reverbConfig?.scheme || 'http';
    // Use forceTLS from config if provided, otherwise calculate from scheme
    const forceTls = reverbConfig?.forceTLS !== undefined 
      ? reverbConfig.forceTLS 
      : reverbScheme === 'https';
    
    // For localhost/http, prioritize ws over wss
    const enabledTransports = reverbScheme === 'http' 
      ? ['ws'] as any  // Only use ws for http
      : (reverbConfig?.enabledTransports as any) || ['ws', 'wss'];

    try {
      this.echo = new Echo({
        broadcaster: 'reverb',
        key: reverbConfig?.appKey || '',
        wsHost: reverbHost,
        wsPort: reverbPort,
        wssPort: reverbPort,
        forceTLS: forceTls,
        enabledTransports: enabledTransports,
        authEndpoint: reverbConfig?.authEndpoint || '/broadcasting/auth',
        auth: {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            Accept: 'application/json',
          },
        },
      });

      this.bindConnectionEvents();

      if (typeof window !== 'undefined' && this.echo) {
        window.Echo = this.echo;
      }
    } catch (error) {
      console.error('Error initializing Echo:', error);
      this.connectionStatus$.next('disconnected');
    }
  }

  private bindConnectionEvents(): void {
    if (!this.echo) {
      return;
    }

    const connector = (this.echo as any).connector;
    const connection = connector?.pusher?.connection;
    if (!connection) {
      return;
    }

    connection.bind('connected', () => {
      this.connectionStatus$.next('connected');
      this.reconnectAttempts = 0; // Reset reconnect attempts on successful connection
      this.clearReconnectTimer();
    });

    connection.bind('disconnected', () => {
      this.connectionStatus$.next('disconnected');
      this.attemptReconnect();
    });

    connection.bind('connecting', () => {
      this.connectionStatus$.next('connecting');
    });

    connection.bind('error', (error: any) => {
      console.error('WebSocket error:', error);
      
      // Check if it's a connection refused error (server not running)
      if (error?.error?.data?.code === 1006 || error?.type === 'WebSocketError') {
        console.error('Reverb server may not be running. Please start it with: php artisan reverb:start');
      }
      
      this.connectionStatus$.next('disconnected');
      this.attemptReconnect();
    });

    connection.bind('unavailable', () => {
      console.error('WebSocket server unavailable');
      this.connectionStatus$.next('disconnected');
      this.attemptReconnect();
    });
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error(`Max reconnect attempts (${this.maxReconnectAttempts}) reached. Stopping reconnection.`);
      return;
    }

    if (this.reconnectTimer) {
      return; // Already attempting to reconnect
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * this.reconnectAttempts; // Exponential backoff

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.reconnect();
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  subscribeToChat(chatId: string | number): void {
    if (!this.echo) {
      this.initializeEcho();
      // Wait a bit for initialization
      setTimeout(() => {
        this.subscribeToChat(chatId);
      }, 1000);
      return;
    }

    const channelName = `chat.${chatId}`;
    
    // Wait for connection before subscribing
    this.waitForConnection().then(() => {
      try {
        const channel = (this.echo as any).private(channelName);

        channel
          .listen('.message.sent', (payload: any) => {
            this.messageSubject$.next(payload);
          })
          .listen('.user.removed', (payload: any) => {
            this.userRemovedSubject$.next(payload);
          })
          .listen('.poll.updated', (payload: any) => {
            this.pollUpdatedSubject$.next(payload);
          })
          .error((error: any) => {
            console.error(`Channel subscription error (${channelName}):`, error);
          });
      } catch (error) {
        console.error(`Error subscribing to channel ${channelName}:`, error);
      }
    }).catch((error) => {
      console.error(`Failed to connect before subscribing to ${channelName}:`, error);
    });
  }

  /**
   * Wait for WebSocket connection to be established
   * @param timeoutMs Maximum time to wait in milliseconds (default: 10 seconds)
   * @returns Promise that resolves when connected
   */
  private waitForConnection(timeoutMs: number = 10000): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isConnected()) {
        resolve();
        return;
      }

      const subscription = this.connectionStatus$
        .pipe(
          filter(status => status === 'connected'),
          take(1),
          timeout(timeoutMs)
        )
        .subscribe({
          next: () => {
            subscription.unsubscribe();
            resolve();
          },
          error: (error) => {
            subscription.unsubscribe();
            reject(new Error('Connection timeout'));
          }
        });

      // If currently disconnected, try to reconnect
      if (this.connectionStatus$.value === 'disconnected') {
        this.connectionStatus$.next('connecting');
      }
      });
  }

  unsubscribeFromChat(chatId: string | number): void {
    if (!this.echo) {
      return;
    }
    this.echo.leave(`chat.${chatId}`);
  }

  subscribeToNotifications(userId: string | number): void {
    if (!this.echo) {
      this.initializeEcho();
      setTimeout(() => {
        this.subscribeToNotifications(userId);
      }, 1000);
      return;
    }

    // Prevent duplicate subscriptions
    if (this.currentNotificationUserId === userId && this.notificationChannel) {
      return;
    }

    // Unsubscribe from previous user if different
    if (this.currentNotificationUserId && this.currentNotificationUserId !== userId) {
      this.unsubscribeFromNotifications();
    }

    const channelName = `notifications.${userId}`;
    this.currentNotificationUserId = userId;

    // Wait for connection before subscribing
    this.waitForConnection().then(() => {
      try {
        this.notificationChannel = (this.echo as any).private(channelName);

        this.notificationChannel
          .listen('.notification.created', (payload: any) => {
            this.notificationSubject$.next(payload);
          })
          .error((error: any) => {
            console.error(`Notification channel subscription error (${channelName}):`, error);
          });
      } catch (error) {
        console.error(`Error subscribing to notification channel ${channelName}:`, error);
      }
    }).catch((error) => {
      console.error(`Failed to connect before subscribing to notification channel ${channelName}:`, error);
    });
  }

  unsubscribeFromNotifications(): void {
    if (!this.echo || !this.currentNotificationUserId) {
      return;
    }
    try {
      this.echo.leave(`notifications.${this.currentNotificationUserId}`);
      this.notificationChannel = null;
      this.currentNotificationUserId = null;
    } catch (error) {
      console.error(`Error unsubscribing from notification channel:`, error);
    }
  }

  onMessage(): Observable<any> {
    return this.messageSubject$.asObservable();
  }

  onNotification(): Observable<any> {
    return this.notificationSubject$.asObservable();
  }

  onUserRemoved(): Observable<any> {
    return this.userRemovedSubject$.asObservable();
  }

  onPollUpdated(): Observable<any> {
    return this.pollUpdatedSubject$.asObservable();
  }

  getConnectionStatus(): Observable<ConnectionState> {
    return this.connectionStatus$.asObservable();
  }

  isConnected(): boolean {
    return this.connectionStatus$.value === 'connected';
  }

  reconnect(): void {
    this.disconnect();
    this.initializeEcho();
  }

  private disconnect(): void {
    this.clearReconnectTimer();
    if (this.echo) {
      this.echo.disconnect();
      this.echo = null;
    }
    this.connectionStatus$.next('disconnected');
  }

  /**
   * Ensure connection is ready before performing operations
   * @returns Promise that resolves when connection is ready
   */
  ensureConnection(): Promise<boolean> {
    if (this.isConnected()) {
      return Promise.resolve(true);
    }

    return this.waitForConnection(5000).then(() => true).catch(() => false);
  }

  ngOnDestroy(): void {
    this.clearReconnectTimer();
    this.unsubscribeFromNotifications();
    this.disconnect();
    this.connectionStatus$.complete();
    this.messageSubject$.complete();
    this.userRemovedSubject$.complete();
    this.pollUpdatedSubject$.complete();
    this.notificationSubject$.complete();
  }
}

